/**
 * Beamify Cart Share
 *
 * "Compartir carrito" on the cart page saves the cart's lines (variant id + quantity + line
 * item properties) on the Beamify backend and shows a short link, https://beamify.mx/cart?c=<code>,
 * with Compartir (native share sheet) and Copiar buttons. Anyone who opens the link gets those
 * lines ADDED to their own cart (their existing items stay) and lands on the cart page with a
 * short note.
 *
 * - Two taps on purpose: iOS only opens the share sheet right after a tap, and saving the cart
 *   first is a network round trip. The second tap (Compartir) is a fresh one.
 * - Properties travel with the lines: customization lines (product_type 'Personalización',
 *   created by the customizer) carry hidden properties such as _base_variant_id that pair them
 *   with their base product. beamify-cart-guard.js relies on that pairing.
 * - Prices never travel: Shopify prices the lines as usual when they're added.
 * - A link is consumed once: its param is stripped from the URL before anything is added, so a
 *   refresh (or back/forward) doesn't add the items twice.
 * - Lines that can't be added (product deleted/unpublished) are skipped and reported.
 * - If the backend can't be reached, sharing falls back to the older self-contained link
 *   (?beamify_cart=<encoded lines>), which is still accepted.
 *
 * Backend: /api/cart-shares on the database backend (beamify_3d_customizer repo,
 * database-backend-deploy/database-server.js). Runs on every page (layout/theme.liquid).
 */
(function () {
  'use strict';

  var API = 'https://beamify-database-backend-772a6721b58d.herokuapp.com/api/cart-shares';
  var SHORT_PARAM = 'c';              // ?c=<code> — only read on the cart page
  var LONG_PARAM = 'beamify_cart';    // older self-contained links
  var RESULT_KEY = 'beamifyCartShareResult';
  var LIMITS = { lines: 50, quantity: 99, props: 30, key: 100, value: 2000 };

  // Localized strings come from layout/theme.liquid; these defaults fill any key a cached,
  // older copy of the page doesn't have yet.
  var strings = Object.assign({
    button: 'Share cart',
    creating: 'Creating link…',
    panelHint: 'Send this link to share your cart:',
    share: 'Share',
    copy: 'Copy',
    copied: 'Copied!',
    shareTitle: 'My cart',
    shareText: 'This is my cart:',
    empty: 'Your cart is empty.',
    error: "We couldn't create the link. Please try again.",
    adding: 'Adding the products from the shared cart…',
    added: 'We added the products from the shared cart to your cart.',
    partial: 'Some products from the shared cart are no longer available and were not added.',
    failed: "The shared cart's products are no longer available.",
    invalid: 'This cart link is no longer valid.'
  }, window.beamifyCartShareStrings || {});

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  // ---------------------------------------------------------------- long-link encoding (UTF-8 safe)
  function encode(lines) {
    var bytes = new TextEncoder().encode(JSON.stringify(lines));
    var binary = '';
    for (var i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decode(code) {
    var b64 = code.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    var binary = atob(b64);
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return JSON.parse(new TextDecoder().decode(bytes));
  }

  /** Keep only well-formed lines ({i, q, p?} → /cart/add.js items); link data is untrusted. */
  function sanitize(data) {
    if (!Array.isArray(data)) return [];
    var out = [];
    for (var i = 0; i < data.length && out.length < LIMITS.lines; i++) {
      var line = data[i] || {};
      var id = String(line.i || '');
      var qty = parseInt(line.q, 10);
      if (!/^\d{1,20}$/.test(id) || !(qty >= 1)) continue;
      var item = { id: Number(id), quantity: Math.min(qty, LIMITS.quantity) };
      if (line.p && typeof line.p === 'object' && !Array.isArray(line.p)) {
        var props = {};
        var count = 0;
        Object.keys(line.p).forEach(function (key) {
          var value = line.p[key];
          if (count >= LIMITS.props || typeof value !== 'string') return;
          if (key.length > LIMITS.key || value.length > LIMITS.value) return;
          props[key] = value;
          count++;
        });
        if (count) item.properties = props;
      }
      out.push(item);
    }
    return out;
  }

  // ---------------------------------------------------------------- sharing (cart page)
  /** The cart's lines as [{i, q, p?}], or null when the cart is empty. */
  function currentLines() {
    return fetch(rootUrl() + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        if (!items.length) return null;
        return items.map(function (item) {
          var line = { i: String(item.variant_id), q: item.quantity };
          var props = item.properties || {};
          var keep = {};
          var any = false;
          Object.keys(props).forEach(function (key) {
            if (props[key] !== null && props[key] !== '') { keep[key] = String(props[key]); any = true; }
          });
          if (any) line.p = keep;
          return line;
        });
      });
  }

  // Links use the bare domain (beamify.mx, not www.beamify.mx) to stay short; Shopify
  // redirects it to the primary domain keeping the path and query.
  function cartUrl(query) {
    var host = window.location.host.replace(/^www\./, '');
    return window.location.protocol + '//' + host + rootUrl() + 'cart?' + query;
  }

  /** Short link via the backend; the self-contained long link if the backend is unreachable. */
  function buildLink(lines) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lines: lines })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var code = body && body.data && body.data.code;
        if (!code) throw new Error('no code');
        return cartUrl(SHORT_PARAM + '=' + encodeURIComponent(code));
      })
      .catch(function (err) {
        console.warn('[BeamifyCartShare] Backend unavailable, using a long link:', err);
        return cartUrl(LONG_PARAM + '=' + encode(lines));
      });
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return fallbackCopy(text); });
    }
    return Promise.resolve(fallbackCopy(text));
  }

  function fallbackCopy(text) {
    try {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.cssText = 'position:fixed;top:0;left:-9999px;';
      document.body.appendChild(area);
      area.select();
      var ok = document.execCommand('copy');
      area.remove();
      return ok;
    } catch (e) {
      return false;
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /** Panel under the share button: the link, Compartir (share sheet, if available) and Copiar. */
  function showPanel(button, url) {
    var old = button.parentNode.querySelector('.beamify-cart-share-panel');
    if (old) old.remove();

    var panel = el('div', 'beamify-cart-share-panel');
    panel.style.cssText = 'margin-top:10px;padding:12px;border:1px solid #e3e6ea;border-radius:8px;background:#f8f9fb;text-align:left;';
    var hint = el('div', '', strings.panelHint);
    hint.style.cssText = 'font-size:13px;color:#555;margin-bottom:8px;';
    // Plain selectable text (not a text field): shows the whole link, wraps if needed, and
    // iOS doesn't zoom into it.
    var linkText = el('div', 'beamify-cart-share-link', url.replace(/^https?:\/\//, ''));
    linkText.style.cssText = 'padding:8px 10px;border:1px solid #ccd2d8;border-radius:6px;background:#fff;color:#222;' +
      'font-size:15px;word-break:break-all;user-select:all;-webkit-user-select:all;';
    function selectLink() {
      var range = document.createRange();
      range.selectNodeContents(linkText);
      var selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }

    var actions = el('div');
    actions.style.cssText = 'display:flex;gap:8px;margin-top:10px;';
    var copyBtn = el('button', 'btn btn-border', strings.copy);
    copyBtn.type = 'button';
    copyBtn.style.cssText = 'flex:1 1 0;margin:0;';
    copyBtn.addEventListener('click', function () {
      copyText(url).then(function (ok) {
        if (!ok) { selectLink(); return; } // let them copy by hand
        copyBtn.textContent = strings.copied;
        setTimeout(function () { copyBtn.textContent = strings.copy; }, 2000);
      });
    });

    if (navigator.share) {
      var shareBtn = el('button', 'btn', strings.share);
      shareBtn.type = 'button';
      shareBtn.style.cssText = 'flex:1 1 0;margin:0;';
      shareBtn.addEventListener('click', function () {
        navigator.share({ title: strings.shareTitle, text: strings.shareText, url: url }).catch(function () { /* closed or unavailable */ });
      });
      actions.appendChild(shareBtn);
    }
    actions.appendChild(copyBtn);

    panel.appendChild(hint);
    panel.appendChild(linkText);
    panel.appendChild(actions);
    button.parentNode.insertBefore(panel, button.nextSibling);
  }

  function onShareClick(evt) {
    var button = evt.currentTarget;
    evt.preventDefault();
    if (button.disabled) return;
    var label = button.textContent;
    button.disabled = true;
    button.textContent = strings.creating;

    // Always from the current cart: it may have changed since the last link.
    currentLines()
      .then(function (lines) {
        if (!lines) { window.alert(strings.empty); return; }
        return buildLink(lines).then(function (url) { showPanel(button, url); });
      })
      .catch(function (err) {
        console.warn('[BeamifyCartShare] Could not build the link:', err);
        window.alert(strings.error);
      })
      .then(function () {
        button.disabled = false;
        button.textContent = label;
      });
  }

  // ---------------------------------------------------------------- receiving
  function stripParams() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete(LONG_PARAM);
      if (isCartPage()) url.searchParams.delete(SHORT_PARAM);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) { /* ignore */ }
  }

  function addLines(items) {
    // One line per request, in order: a line that can't be added (product deleted or
    // unpublished) is skipped without affecting the others. A single batch request isn't used
    // because retrying after a partly-applied batch could add lines twice.
    var added = 0;
    var chain = Promise.resolve();
    items.forEach(function (item) {
      chain = chain.then(function () {
        return postAdd([item]).then(function (ok) { if (ok) added++; });
      });
    });
    return chain.then(function () { return { added: added, failed: items.length - added }; });
  }

  function postAdd(items) {
    return fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) { return res.ok; }, function () { return false; });
  }

  /** Lines for a short code from the backend; null if the code doesn't exist. */
  function fetchSharedLines(code) {
    return fetch(API + '/' + encodeURIComponent(code))
      .then(function (res) {
        if (res.status === 404) return null;
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json().then(function (body) { return (body && body.data && body.data.lines) || null; });
      });
  }

  function receive() {
    var params;
    try { params = new URL(window.location.href).searchParams; } catch (e) { return false; }
    var shortCode = isCartPage() ? params.get(SHORT_PARAM) : null;
    var longCode = params.get(LONG_PARAM);
    if (!shortCode && !longCode) return false;
    stripParams(); // consumed once, whatever happens next
    showNote(strings.adding, 'info');

    var linesPromise = shortCode
      ? fetchSharedLines(shortCode)
      : Promise.resolve().then(function () { return decode(longCode); });

    linesPromise
      .then(function (lines) {
        if (lines === null) return { invalid: true };
        var items = sanitize(lines);
        return items.length ? addLines(items) : { invalid: true };
      }, function (err) {
        console.warn('[BeamifyCartShare] Could not read the shared cart:', err);
        return { invalid: true };
      })
      .then(function (result) {
        try { sessionStorage.setItem(RESULT_KEY, JSON.stringify(result)); } catch (e) { /* ignore */ }
        // Reload the cart page so every cart widget (header count, totals, guard) is fresh.
        window.location.replace(rootUrl() + 'cart');
      });
    return true;
  }

  // ---------------------------------------------------------------- notes on the cart page
  function showNote(message, tone) {
    var old = document.querySelector('.beamify-cart-share-note');
    if (old) old.parentNode.remove();

    var note = el('div', 'beamify-cart-share-note', message);
    note.setAttribute('role', 'status');
    var colors = {
      ok: 'background:#edf7ee;color:#1e6b2e;border:1px solid #bfe3c6;',
      warn: 'background:#fdf6e7;color:#8a5a00;border:1px solid #f0dcaa;',
      info: 'background:#eef4fb;color:#1d4f86;border:1px solid #c8dcf1;'
    };
    note.style.cssText = 'margin:0 0 16px;padding:12px 14px;border-radius:6px;font-size:14px;line-height:1.4;' + (colors[tone] || colors.info);

    // Above the cart (sections/cart-template.liquid), or above the "cart is empty" block.
    var wrapper = el('div', 'container');
    wrapper.style.marginTop = '20px';
    wrapper.appendChild(note);
    var anchor = document.querySelector('.line-items-box-js:not(.hide)') ||
      document.querySelector('.tt-empty-cart-js:not(.hide)');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(wrapper, anchor);
    else (document.querySelector('main') || document.body).prepend(wrapper);
  }

  function showResultNote() {
    var raw;
    try { raw = sessionStorage.getItem(RESULT_KEY); sessionStorage.removeItem(RESULT_KEY); } catch (e) { return; }
    if (!raw) return;
    var result;
    try { result = JSON.parse(raw); } catch (e) { return; }
    if (result.invalid) showNote(strings.invalid, 'warn');
    else if (result.added && !result.failed) showNote(strings.added, 'ok');
    else if (result.added) showNote(strings.partial, 'warn');
    else showNote(strings.failed, 'warn');
  }

  function init() {
    if (receive()) return; // adding, then reloading the cart page
    showResultNote();
    var buttons = document.querySelectorAll('[data-beamify-share-cart]');
    for (var i = 0; i < buttons.length; i++) buttons[i].addEventListener('click', onShareClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
